'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { adminService } from '@/lib/services/adminService';
import Image from 'next/image';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const pathname = usePathname();
  const { user, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || loading) {
        setIsAdmin(false);
        return;
      }
      try {
        const adminStatus = await adminService?.isAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Admin check error:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, loading]);

  const baseNavLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_gallery', label: 'Gallery', href: '/gallery' },
    { id: 'nav_about', label: 'About', href: '/about' },
    { id: 'nav_client', label: 'Client Access', href: '/client-access' },
  ];

  const navLinks = baseNavLinks;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-panel shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-500 group-hover:rotate-[360deg]">
              <Image
                src="/logo-nobg.png"
                alt="SukeMedia Logo"
                width={70}
                height={70}
                style={{ maxWidth: 'none' }}
                className="object-contain"
              />
              {/* <Icon name="CameraIcon" size={24} className="text-background" /> */}
            </div>
            <span className="text-xl font-semibold tracking-tight text-white"></span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                className={`text-sm font-medium tracking-wide transition-colors relative group ${
                  pathname === link?.href ? 'text-white' : 'text-muted-foreground hover:text-white'
                }`}
              >
                {link?.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-white transition-all duration-300 ${
                    pathname === link?.href ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <Link
            href="/homepage#contact"
            className="hidden md:flex items-center gap-2 px-6 py-2.5 border border-white/20 rounded-lg text-sm font-medium text-white hover:bg-white hover:text-background transition-all duration-300"
          >
            Book Session
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-white"
            aria-label="Toggle menu"
          >
            <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 glass-panel rounded-lg p-4 space-y-2">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  pathname === link?.href
                    ? 'bg-white/10 text-white'
                    : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href="/homepage#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full px-4 py-3 mt-2 border border-white/20 rounded-lg text-sm font-medium text-center text-white hover:bg-white hover:text-background transition-all"
            >
              Book Session
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
